import type { Component } from 'vue';
import {
    type ActiveModal,
    type ExtractModalResult,
    type ModalOptions,
    closeModal,
    openModal,
    useModalRenderer,
} from 'src/util/composables/useModal';

// Re-export types under the legacy names so existing imports keep working
export type { ActiveModal, ExtractModalResult };
export type EntityModalOptions<
    C extends Component,
    RETURN extends ExtractModalResult<C>,
> = ModalOptions<C, RETURN>;

export interface EntityModalController {
    /**
     * Opens a modal and returns a promise that resolves when the modal is closed.
     * Props type is automatically inferred from the component when possible.
     * @param options Modal configuration
     * @returns Promise that resolves with the result value, or null if cancelled
     * @template T The type of the result returned when the modal closes
     * @example
     * // Props are inferred from MyModal.vue:
     * const result = await openModal<{ confirmed: boolean }>({
     *   component: () => import('./MyModal.vue'),
     *   props: { title: 'Confirm Action' },  // ← type-checked against MyModal's props
     * });
     */
    openModal: <C extends Component, RETURN extends ExtractModalResult<C>>(
        options: EntityModalOptions<C, RETURN>
    ) => Promise<RETURN | null>;
    /**
     * Closes the currently open modal with an optional result value.
     * @param result Value to resolve the promise with
     */
    closeModal: (result?: unknown) => void;
}

/**
 * Returns the global modal state for use in the top-level rendering component.
 * Previously set up a scoped provide/inject; now delegates to the app-level singleton.
 */
export function useEntityModalProvider () {
    return useModalRenderer();
}

/**
 * Returns the modal controller to programmatically open/close the global modal.
 * No longer requires an ancestor RouteEntityDetail — works anywhere in the app.
 */
export function useEntityModal (): EntityModalController {
    return { openModal, closeModal };
}
